import React, { useState } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import AppTableContainer from '@projectK/components/AppTableContainer';
import TableHeading from './TableHeading';
import TableItem from './TableItem';
import PropTypes from 'prop-types';
import { Box, Button, Typography, alpha } from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import AppsPagination from '@projectK/components/AppsPagination';
import IntlMessages from '@projectK/helpers/IntlMessages';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Fonts } from '@projectK/constants';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import AddAccountMembers from './AddAccountMembers';
import AppCard from '@projectK/components/AppCard';
import AddExisitingAccountMember from './AddExisitingAccountMember';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import { ButtonMain } from '@projectK/MainComponents';
import {isActualSuperAdmin,isActualAdmin } from '@projectK/context/RolesProvider';

const AccountMembers = ({
  fetchMembersData,
  settings,
  total,
  page,
  setPage,
}) => {
  const { isMobile } = useMobileSettings();
  const companyTheme = CompanyTheme();
  const { verbiageSettings } = useApplicationSettings();
  const infoViewActionsContext = useInfoViewActionsContext();
  const mobileResponse = useMediaQuery('(max-width:755px)');
  const { user } = useAuthUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExistingMemberModalOpen, setIsExistingMemberModalOpen] =
    useState(false);
    

  const handleDelete = (userId) => {
    jwtAxios
      .delete(`/users/in-active/${userId}`)
      .then((response) => {
        fetchMembersData();
        infoViewActionsContext.showMessage('Member deleted successfully!');
      })
      .catch((error) => {
        infoViewActionsContext.fetchError('Error deleting user');
        console.error('Error deleting user:', error);
      });
  };
  const onPageChange = (event, value) => {
    setPage(value);
  };


  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openExistingModal = () => {
    setIsExistingMemberModalOpen(true);
  };
  const closeExistingModal = () => {
    setIsExistingMemberModalOpen(false);
  };

  const table = (
    <AppTableContainer>
      <Table className='table'>
        <TableHead>
          <TableHeading user={user} />
        </TableHead>
        <TableBody>
          {settings?.map((item) => (
            <TableItem
              key={item.id}
              data={item}
              onDelete={handleDelete}
              fetchMembersData={fetchMembersData}
              companyTheme={companyTheme}
            />
          ))}
        </TableBody>
      </Table>
    </AppTableContainer>
  );
  const primaryColor = companyTheme ? companyTheme.primary.main : '#2196f3';
  return (
    <AppCard>
      <Typography
        component='h3'
        sx={{
          marginBottom: 2,
          fontSize: 16,
          fontWeight: Fonts.BOLD,
          color: companyTheme ? companyTheme.primary.main : 'text.primary',
        }}
      >
        {verbiageSettings?.account_member_alias || (
          <IntlMessages id='common.accountMembers' />
        )}
      </Typography>
      <Typography component='h3' sx={{ marginBottom: 5 }}>
        These settings let you add family members (ex: children) that can be
        managed by your account.
      </Typography>

      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        width={1}
        justifyContent={mobileResponse ? 'space-between' : 'end'}
        marginBottom={5}
      >
        <Box display='flex' flexDirection='row' alignItems='center'>
          {(isActualSuperAdmin(user) ||(isActualAdmin(user))) ? (
            <Button
              variant='outlined'
              size='small'
              onClick={openExistingModal}
              sx={{
                position: 'relative',
                ml: 2.5,
                ...(mobileResponse ? { minWidth: 0 } : { minWidth: 100 }),
                borderColor: primaryColor,
                '&:hover': {
                  borderColor: alpha(primaryColor, 0.8),
                },
                color: primaryColor,
              }}
            >
              <AddOutlinedIcon sx={{ mr: 1 }} />
              Add Existing Member
            </Button>
          ) : (
            ''
          )}
          <ButtonMain
            variant='outlined'
            size='small'
            onClick={openModal}
            sx={{
              position: 'relative',
              marginLeft: '10px',
              ...(mobileResponse ? { minWidth: 0 } : { minWidth: 100 }),
              borderColor: primaryColor,
              '&:hover': {
                borderColor: alpha(primaryColor, 0.8),
              },
              color: primaryColor,
            }}
            name={
              <>
                <AddOutlinedIcon sx={{ mr: 1 }} />
                Add Member
              </>
            }
            color='primary'
            fill='outline'
            style={{
              marginBottom: 'inherit',
              borderColor: primaryColor,
              color: primaryColor,
            }}
          />
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <AppsPagination
              rowsPerPage={5}
              count={total}
              page={page}
              onPageChange={onPageChange}
            />
          </Box>
        </Box>
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <AppsPagination
            rowsPerPage={5}
            count={total}
            page={page}
            onPageChange={onPageChange}
          />
        </Box>
      </Box>
      <AddAccountMembers
        isOpen={isModalOpen}
        onClose={closeModal}
        fetchMembersData={fetchMembersData}
      />
      {isExistingMemberModalOpen && (<AddExisitingAccountMember
        isOpen={isExistingMemberModalOpen}
        onClose={closeExistingModal}
        fetchMembersData={fetchMembersData}
      />)}
      {isMobile ? <div className='mobileapp'>{table}</div> : table}
    </AppCard>
  );
};

export default AccountMembers;

AccountMembers.propTypes = {
  settings: PropTypes.array,
  fetchMembersData: PropTypes.func,
  total: PropTypes.number,
  page: PropTypes.number,
  setPage: PropTypes.func,
};
